import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { useNbTheme } from '../hooks/useNbTheme';
import NavBarNB from '../components/NavBarNB';
import FooterNB from '../components/FooterNB';
import CardNB from '../components/ui-nb/CardNB';
import ButtonNB from '../components/ui-nb/ButtonNB';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { getErrorMessage } from '../lib/errors';
import { error as logError } from '../lib/logger';

export default function LoginPage() {
  const { t, language } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const configured = isSupabaseConfigured();
  const { colors } = useNbTheme();

  const handleGoogleSignIn = async () => {
    if (!configured) {
      setError(t('login.notConfigured'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // For web, use HTTP/HTTPS callback URL
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const webCallbackUrl = isLocalhost 
        ? `${window.location.origin}/en/auth/callback`
        : `${window.location.origin}/en/auth/callback`;

      const { data, error: signInError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: webCallbackUrl,
          scopes: 'email profile',
          skipBrowserRedirect: false,
        },
      });

      if (signInError) throw signInError;
    } catch (err) {
      logError('Sign in error', { error: getErrorMessage(err) });
      setError(t('login.error'));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--nb-bg-page)' }}>
      <NavBarNB />
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <CardNB className="w-full max-w-md">
          <h1 className="text-3xl font-bold text-center mb-2" style={{ color: colors.textPrimary }}>
            {t('login.title')}
          </h1>
          <p className="text-center mb-8" style={{ color: colors.textSecondary }}>
            {t('login.subtitle')}
          </p>

          {error && (
            <div 
              className="mb-6 px-4 py-3 border-l-4 border-nb-red"
              style={{ 
                backgroundColor: 'rgba(239, 68, 68, 0.1)', 
                color: '#ef4444'
              }}
            >
              {error}
            </div>
          )}

          <label className="flex items-center gap-2 text-left mb-6 cursor-pointer">
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              className="w-4 h-4 rounded-[2px] accent-nb-yellow"
            />
            <span className="text-sm" style={{ color: colors.textSecondary }}>
              I agree to the{' '}
              <Link to={`/${language}/terms`} target="_blank" className="hover:underline font-medium" style={{ color: colors.textPrimary }}>Terms of Service</Link>
              {' '}and{' '}
              <Link to={`/${language}/privacy`} target="_blank" className="hover:underline font-medium" style={{ color: colors.textPrimary }}>Privacy Policy</Link>
            </span>
          </label>

          <ButtonNB
            variant="secondary"
            onClick={handleGoogleSignIn}
            disabled={loading || !consentChecked}
            className="w-full"
            size="lg"
          >
            {loading ? (
              <span className="inline-block w-5 h-5 border-3 border-current border-t-transparent animate-spin" />
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                {t('login.cta')}
              </>
            )}
          </ButtonNB>

          <p className="mt-6 text-center text-sm" style={{ color: colors.textMuted }}>
            {t('login.noAccount')}{' '}
            <Link to={`/${language}/register`} className="hover:underline font-medium" style={{ color: colors.textPrimary }}>
              {t('login.register')}
            </Link>
          </p>
        </CardNB>
      </main>
      <FooterNB />
    </div>
  );
}
